import { useState, useEffect } from "react";
import Select from "react-select";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./AddCostModal.css";
import axiosInstance from "../../../Config/axiosInstance";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";

//initializare date
const initialFormData = {
  project_code: "",
  department: "",
  resource_type: "employer",
  resource: "",
  vat_payer: "yes",
  price_without_vat: "",
  vat: "21",
  price_with_vat: "",
  remarks: "",
  budget_type: "",
  expense_type: "",
  month: "",
  year: "",
  pay_date: "",
  registration_date: "",
  registration_date_display: "",
  document: null,
};

function AddCostModal({ open, onClose, onSave }) {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  //Functie inchidere modal 
  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  //Resetează formularul și setează automat data curentă la fiecare deschidere a modalului
  useEffect(() => {
    if (open) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");

      // Schimbat de la "/" la "." pentru formatul de afișare
      const displayDate = `${dd}.${mm}.${yyyy}`;
      const isoDate = `${yyyy}-${mm}-${dd}`;

      setFormData({
        ...initialFormData,
        registration_date: isoDate,
        registration_date_display: displayDate,
      });
    }
  }, [open]);

  // Map Project Codes
  const [projectCodes, setProjectCodes] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/project-codes/")
      .then((response) => {
        const formattedProjectCodes = response.data.map((pc) => ({
          value: pc.id,
          label: `${pc.code} - ${pc.description}`,
        }));
        setProjectCodes(formattedProjectCodes);
      })
      .catch((error) =>
        console.error("Eroare la preluarea codurilor de proiect:", error)
      );
  }, []);

  // Map Departments
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/departments/")
      .then((response) => {
        const formattedDepartments = response.data.map((dept) => ({
          value: dept.id,
          label: dept.name,
        }));
        setDepartments(formattedDepartments);
      })
      .catch((error) =>
        console.error("Eroare la preluarea departamentelor:", error)
      );
  }, []);

  // Map Budget Types (Active only)
  const [budgetTypes, setBudgetTypes] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/budget-type/")
      .then((response) => {
        const formattedBudgetTypes = response.data
          .filter((bt) => bt.status === "Active")
          .map((bt) => ({
            value: bt.id,
            label: bt.type,
          }));
        setBudgetTypes(formattedBudgetTypes);
      })
      .catch((error) =>
        console.error("Eroare la preluarea tipurilor de buget:", error)
      );
  }, []);

  // Map Expense Types (Active only)
  const [expenseTypes, setExpenseTypes] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/expenses-type/")
      .then((response) => {
        const formattedExpenseTypes = response.data
          .filter((et) => et.status?.toLowerCase() === "active")
          .map((et) => ({
            value: et.id,
            label: et.type,
          }));
        setExpenseTypes(formattedExpenseTypes);
      })
      .catch((error) =>
        console.error("Eroare la preluarea tipurilor de cheltuieli:", error)
      );
  }, []);

  // Map Suppliers
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/suppliers/")
      .then((response) => {
        const supplierOptions = response.data.map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
        }));
        setSuppliers(supplierOptions);
      })
      .catch((error) => console.error("Error fetching suppliers:", error));
  }, []);

  // Map Employees
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/employees/")
      .then((response) => {
        const employeeOptions = response.data.map((emp) => ({
          value: emp.id,
          label: `${emp.first_name} ${emp.last_name}`,
        }));
        setEmployees(employeeOptions);
      })
      .catch((error) => console.error("Error fetching employees:", error));
  }, []);

  // Map Month choices
  const [monthChoices, setMonthChoices] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/month-choices/")
      .then((response) => {
        const data = response.data;
        if (Array.isArray(data.month_choices)) {
          const options = data.month_choices.map(([value, label]) => ({
            value,
            label,
          }));
          setMonthChoices(options);
        }
      })
      .catch((error) => console.error("Eroare la preluarea lunilor:", error));
  }, []);

  // Map Year choices
  const [yearChoices, setYearChoices] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/year-choices/")
      .then((response) => {
        const data = response.data;
        if (Array.isArray(data.year_choices)) {
          const options = data.year_choices.map(([value, label]) => ({
            value,
            label,
          }));
          setYearChoices(options);
        }
      })
      .catch((error) => console.error("Eroare la preluarea anilor:", error));
  }, []);

  //Set Data curentă
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    // Schimbat de la "/" la "." pentru formatul de afișare
    const displayDate = `${dd}.${mm}.${yyyy}`;
    const isoDate = `${yyyy}-${mm}-${dd}`;

    setFormData((prev) => ({
      ...prev,
      registration_date: isoDate,
      registration_date_display: displayDate,
    }));
  }, []);

  // Auto set VAT 
  useEffect(() => {
    if (formData.vat_payer === "yes") {
      setFormData((prev) => ({
        ...prev,
        vat: prev.vat || 21,
      }));
    } else if (formData.vat_payer === "no") {
      setFormData((prev) => ({
        ...prev,
        vat: 0,
      }));
    }
  }, [formData.vat_payer]);

  // Handle Change
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      // salvează fișierul selectat
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      return;
    }

    const numericFields = [
      "project_code",
      "department",
      "budget_type",
      "expense_type",
      "month",
      "year",
      "resource",
    ];

    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? Number(value) : value,
    }));

    // șterge eroarea dacă a fost completat corect
    setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "vat_payer" && value === "no") {
      setErrors((prev) => ({ ...prev, vat: "" }));
    }
  };

  // Reset resource când se schimbă tipul resursei
  useEffect(() => {
    setFormData((prev) => ({ ...prev, resource: "" }));
  }, [formData.resource_type]);

  // Actualizează automat resource când tipul e "department" (preia departamentul)
  useEffect(() => {
    if (formData.resource_type === "department") {
      const selectedDept = departments.find(
        (d) => d.value === formData.department
      );
      setFormData((prev) => ({
        ...prev,
        resource: selectedDept ? selectedDept.label : "",
      }));
    }
  }, [formData.department, formData.resource_type, departments]);

  // Calculează automat price_with_vat
  useEffect(() => {
    const withoutVat = parseFloat(formData.price_without_vat) || 0;
    const vatValue = parseFloat(formData.vat) || 0;

    const calculated = withoutVat + (withoutVat * vatValue) / 100;

    setFormData((prev) => ({
      ...prev,
      price_with_vat: calculated ? calculated.toFixed(2) : "",
    }));
  }, [formData.price_without_vat, formData.vat]);

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_code) newErrors.project_code = "Project Code is required.";
    if (!formData.department) newErrors.department = "Department is required.";
    if (!formData.resource) newErrors.resource = "Resource is required.";
    if (!formData.price_without_vat) newErrors.price_without_vat = "Price without VAT is required.";
    if (formData.vat_payer === "yes" && (!formData.vat || formData.vat <= 0))
      newErrors.vat = "VAT is required when VAT Payer is 'Yes'.";
    if (!formData.remarks) newErrors.remarks = "Remarks are required.";
    if (!formData.budget_type) newErrors.budget_type = "Budget Type is required.";
    if (!formData.expense_type) newErrors.expense_type = "Expense Type is required.";
    if (!formData.month) newErrors.month = "Month is required.";
    if (!formData.year) newErrors.year = "Year is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // Save (cu upload fișier)
  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);

    if (formData.vat_payer === "yes" && (!formData.vat || formData.vat <= 0)) {
      alert("VAT trebuie completat dacă VAT Payer este 'Yes'.");
      setLoading(false);
      return;
    }

    try {
      // Payload-ul de bază
      const payload = {
        vat_payer: formData.vat_payer === "yes",
        without_vat: formData.price_without_vat?.toString() || "0.00",
        vat: parseFloat(formData.vat || 0),
        with_vat: formData.price_with_vat?.toString() || "0.00",
        remarks: formData.remarks || "",
        month: formData.month,
        year: parseInt(formData.year || new Date().getFullYear()),
        pay_date: formData.pay_date ? formData.pay_date : null,
        registration_date:
          formData.registration_date || new Date().toISOString().split("T")[0],
        project_code: formData.project_code || null,
        department: formData.department || null,
        budget_type: formData.budget_type || null,
        expenses_type: formData.expense_type || null,
        user: 1,
      };

      // Logica pentru resource
      if (formData.resource_type === "supplier") {
        payload.supplier = formData.resource || null;
        payload.resource = null;
        payload.department_resource = null;
      } else if (formData.resource_type === "employer") {
        payload.resource = formData.resource || null;
        payload.supplier = null;
        payload.department_resource = null;
      } else if (formData.resource_type === "department") {
        payload.department_resource = formData.department || null;
        payload.resource = null;
        payload.supplier = null;
      }

      // Construim un obiect FormData (pentru fișier + câmpuri)
      const formPayload = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formPayload.append(key, value);
        }
      });

      // Adăugăm fișierul dacă există
      if (formData.document) {
        formPayload.append("document", formData.document);
      }

      // Trimitem requestul ca multipart/form-data
      const response = await axiosInstance.post("/cost-administrative/", formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("✅ Cost salvat cu succes:", response.data);
      if (onSave) onSave(response.data);
      onClose();
    } catch (error) {
      console.error("❌ Eroare la salvare:", error.response?.data || error);
      alert("Eroare la salvare — verifică consola.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="add-cost-container"
    >
      <DialogTitle
        className="title-popup"
        sx={{ position: "relative", paddingRight: "40px" }}
      >
        Add Cost
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#555",
            "&:hover": { color: "black" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          {/*  Project Code */}
          <div className="form-group small">
            <label className="label">
              Project Code <span className="required">*</span>
            </label>
            <Select
              name="project_code"
              options={projectCodes}
              placeholder="Select project code"
              isClearable
              classNamePrefix="react-select"
              className={errors.project_code ? "select-error" : ""}
              value={
                projectCodes.find((pc) => pc.value === formData.project_code) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  project_code: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, project_code: "" }));
              }}
            />
            {errors.project_code && (
              <p className="error-text">{errors.project_code}</p>
            )}
          </div>

          {/* Department */}
          <div className="form-group small">
            <label className="label">
              Department <span className="required">*</span>
            </label>
            <Select
              name="department"
              options={departments}
              placeholder="Select department"
              isClearable
              classNamePrefix="react-select"
              className={errors.department ? "select-error" : ""}
              value={
                departments.find((d) => d.value === formData.department) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  department: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, department: "" }));
              }}
            />
            {errors.department && (
              <p className="error-text">{errors.department}</p>
            )}
          </div>
        </div>

        {/*Resource Type*/}
        <div className="form-group">
          <label className="label">Resource Type<span className="required">*</span></label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="resource_type"
                value="employer"
                checked={formData.resource_type === "employer"}
                onChange={handleChange}
              />{" "}
              Employer
            </label>
            <label>
              <input
                type="radio"
                name="resource_type"
                value="department"
                checked={formData.resource_type === "department"}
                onChange={handleChange}
              />{" "}
              Department Resource
            </label>
            <label>
              <input
                type="radio"
                name="resource_type"
                value="supplier"
                checked={formData.resource_type === "supplier"}
                onChange={handleChange}
              />{" "}
              Supplier
            </label>
          </div>
        </div>

        {/* Resource*/}
        <div className="form-group small">
          <label className="label">
            Resource <span className="required">*</span>
          </label>

          {formData.resource_type === "supplier" ? (
            <Select
              name="resource"
              options={suppliers}
              placeholder="Search supplier..."
              isClearable
              isSearchable
              classNamePrefix="react-select"
              className={errors.resource ? "select-error" : ""}
              value={suppliers.find((s) => s.value === formData.resource) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  resource: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, resource: "" }));
              }}
            />
          ) : formData.resource_type === "employer" ? (
            <Select
              name="resource"
              options={employees}
              placeholder="Select employee..."
              isClearable
              isSearchable
              classNamePrefix="react-select"
              className={errors.resource ? "select-error" : ""}
              value={employees.find((e) => e.value === formData.resource) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  resource: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, resource: "" }));
              }}
            />
          ) : (
            <input
              type="text"
              name="resource"
              className="input"
              readOnly
              value={
                formData.resource ||
                departments.find((d) => d.value === formData.department)?.label ||
                ""
              }
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
              onChange={() => setErrors((prev) => ({ ...prev, resource: "" }))}
            />
          )}
          {errors.resource && <p className="error-text">{errors.resource}</p>}
        </div>



        {/*  VAT Payer */}
        <div className="form-group">
          <label className="label">VAT Payer</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="vat_payer"
                value="yes"
                checked={formData.vat_payer === "yes"}
                onChange={handleChange}
              />{" "}
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="vat_payer"
                value="no"
                checked={formData.vat_payer === "no"}
                onChange={handleChange}
              />{" "}
              No
            </label>
          </div>
        </div>

        {/* Prices without VAT*/}
        <div className="row">
          <div className="form-group small">
            <label className="label">
              Price without VAT <span className="required">*</span>
            </label>
            <NumericFormat
              name="price_without_vat"
              value={formData.price_without_vat}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale={false}
              allowNegative={false}
              customInput="input"
              className={`input ${errors.price_without_vat ? "input-error" : ""}`}
              onValueChange={(values) => {
                handleChange({
                  target: { name: "price_without_vat", value: values.floatValue ?? "" },
                });
                setErrors((prev) => ({ ...prev, price_without_vat: "" }));
              }}
            />

            {errors.price_without_vat && (
              <p className="error-text">{errors.price_without_vat}</p>
            )}
          </div>
          <div className="form-group small">
            <label className="label">
              VAT{" "}
              {formData.vat_payer === "yes" && (
                <span className="required">*</span>
              )}
            </label>

            <input
              type="number"
              name="vat"
              className={`input ${errors.vat && formData.vat_payer === "yes" ? "input-error" : ""
                }`}
              value={formData.vat}
              onChange={(e) => {
                handleChange(e);
                setErrors((prev) => ({ ...prev, vat: "" }));
              }}
              required={formData.vat_payer === "yes"}
              readOnly={formData.vat_payer === "no"}
              style={{
                backgroundColor:
                  formData.vat_payer === "no" ? "#eeeeee" : "white",
                cursor:
                  formData.vat_payer === "no" ? "not-allowed" : "auto",
              }}
            />
            {errors.vat && <p className="error-text">{errors.vat}</p>}
          </div>


          <div className="form-group small">
            <label className="label">Price with VAT</label>
            <NumericFormat
              name="price_with_vat"
              value={formData.price_with_vat}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              customInput="input"
              className="input"
              readOnly
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
            />
          </div>
        </div>

        {/*Remarks  */}
        <div className="form-group">
          <label className="label">
            Remarks <span className="required">*</span>
          </label>
          <textarea
            name="remarks"
            className={`input textarea ${errors.remarks ? "input-error" : ""}`}
            value={formData.remarks}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, remarks: "" }));
            }}
            placeholder="Add remarks..."
          />
          {errors.remarks && <p className="error-text">{errors.remarks}</p>}
        </div>

        <div className="row">
          {/*  Budget Type */}
          <div className="form-group small">
            <label className="label">
              Budget Type <span className="required">*</span>
            </label>
            <Select
              name="budget_type"
              options={budgetTypes}
              placeholder="Select budget type"
              classNamePrefix="react-select"
              className={errors.budget_type ? "select-error" : ""}
              value={
                budgetTypes.find((bt) => bt.value === formData.budget_type) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  budget_type: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, budget_type: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.budget_type && (
              <p className="error-text">{errors.budget_type}</p>
            )}
          </div>

          {/* Expense Type */}
          <div className="form-group small">
            <label className="label">
              Expense Type <span className="required">*</span>
            </label>
            <Select
              name="expense_type"
              options={expenseTypes}
              placeholder="Select expense type"
              classNamePrefix="react-select"
              className={errors.expense_type ? "select-error" : ""}
              value={
                expenseTypes.find((et) => et.value === formData.expense_type) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  expense_type: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, expense_type: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.expense_type && (
              <p className="error-text">{errors.expense_type}</p>
            )}
          </div>

          <div className="form-group small">
            <label className="label">Registration Date</label>
            <input
              type="text"
              name="registration_date_display"
              className="input"
              value={formData.registration_date_display}
              readOnly
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
            />
          </div>
        </div>
        <div className="row">
          {/* Month */}
          <div className="form-group small">
            <label className="label">
              Month <span className="required">*</span>
            </label>
            <Select
              name="month"
              options={monthChoices}
              placeholder="Select month"
              classNamePrefix="react-select"
              className={errors.month ? "select-error" : ""}
              value={monthChoices.find((m) => m.value === formData.month) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  month: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, month: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.month && <p className="error-text">{errors.month}</p>}
          </div>

          {/*  Year */}
          <div className="form-group small">
            <label className="label">
              Year <span className="required">*</span>
            </label>
            <Select
              name="year"
              options={yearChoices}
              placeholder="Select year"
              classNamePrefix="react-select"
              className={errors.year ? "select-error" : ""}
              value={yearChoices.find((y) => y.value === formData.year) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  year: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, year: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.year && <p className="error-text">{errors.year}</p>}
          </div>

          {/* Pay Date - CU ICONIȚĂ DE CALENDAR */}
          <div className="form-group small">
            <label className="label">Pay Date</label>
            <div className="datepicker-container-cost">
              <DatePicker
                selected={formData.pay_date ? new Date(formData.pay_date) : null}
                onChange={(date) => {
                  if (date) {
                    setFormData((prev) => ({
                      ...prev,
                      pay_date: format(date, "yyyy-MM-dd"),
                    }));
                  } else {
                    setFormData((prev) => ({ ...prev, pay_date: "" }));
                  }
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="dd.mm.yyyy"
                className="datepicker-input-cost"
                calendarStartDay={1}
                popperPlacement="bottom-start"
              />
              <span className="calendar-icon-cost">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/*  Document */}
        <div className="form-group">
          <label className="label">Documents</label>
          <div className="upload-container">
            <input
              type="file"
              id="file-upload"
              name="document"
              onChange={handleChange}
              style={{ display: "none" }}
            />

            <Button
              variant="outlined"
              onClick={() => document.getElementById("file-upload").click()}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Choose File
            </Button>

            {formData.document && (
              <div className="file-preview" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span className="file-name" style={{ fontSize: "14px", color: "#333" }}>
                  {formData.document.name}
                </span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove file"
                  onClick={() => setFormData((prev) => ({ ...prev, document: null }))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} className="button discard">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          className="button save"
          disabled={loading}
        >
          {loading ? "Saving..." : "Done"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddCostModal;